import React from 'react';
import ReactMarkdown from 'react-markdown';
import { FaSpinner } from 'react-icons/fa';
import { DataRenderer } from './DataRenderer';

export const MyResultCard = ({ 
  result, 
  index, 
  totalResults,
  expandedStatistics,
  publicResults,
  togglingResults,
  cleanupRef,
  onToggleStatistics,
  onTogglePublic,
  onRemoveResult,
  onUseQuery,
  setIsLoading,
  setStatusMessage,
  streamingDescription = ''
}) => {
  const resultId = result.id;
  const isStatisticsExpanded = expandedStatistics[resultId] || false;
  const isPublic = Boolean(publicResults[resultId] && typeof publicResults[resultId] === 'number');
  const isToggling = togglingResults[resultId] || false;
  
  const toggleStatistics = () => {
    onToggleStatistics(resultId);
  };
  
  const displayNumber = totalResults - index;
  
  const processedContent = {
    descriptions: [],
    charts: [],
    statistics: null
  };
  
  result.outputs.forEach(output => {
    if (output.generate_chart && output.generate_chart.chart) {
      processedContent.charts.push(output.generate_chart.chart);
    }
    if (output.write_description && output.write_description.description) {
      processedContent.descriptions.push(output.write_description.description);
    }
    
    if (output.chart) {
      processedContent.charts.push(output.chart);
    }
    if (output.description) {
      processedContent.descriptions.push(output.description);
    }
    if (output.fetch_statistics) {
      processedContent.statistics = output.fetch_statistics;
    }
  });
  
  return (
    <div className={`bg-white rounded-lg shadow p-6 w-full mb-6 ${result.isInProgress ? 'border-l-4 border-blue-500' : ''}`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          #{displayNumber}
          {result.isInProgress && <span className="text-blue-500 ml-2">(In Progress)</span>}
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {isToggling ? 'Updating...' : (isPublic ? 'Make private' : 'Make public')}
            </span>
            <button
              role="switch"
              aria-checked={isPublic}
              onClick={() => onTogglePublic(resultId)}
              disabled={isToggling}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isToggling 
                  ? 'bg-gray-300 cursor-not-allowed'
                  : isPublic 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {isToggling ? (
                <FaSpinner className="animate-spin h-3 w-3 text-gray-500 mx-auto" />
              ) : (
                <span 
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                    isPublic ? 'translate-x-6' : 'translate-x-1'
                  }`} 
                />
              )}
            </button>
          </div>

          {result.isInProgress ? (
            <button 
              onClick={() => {
                if (index === 0 && cleanupRef.current) {
                  cleanupRef.current();
                  cleanupRef.current = null;
                  setIsLoading(false);
                  setStatusMessage('');
                }
                onRemoveResult(resultId);
              }}
              className="p-2 rounded-full text-sm transition-colors bg-red-100 text-red-600 hover:bg-red-200"
              title="Cancel and remove this result"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : (
            <button 
              onClick={() => onRemoveResult(resultId)}
              className="p-2 rounded-full text-sm transition-colors bg-red-100 text-red-600 hover:bg-red-200"
              title="Delete this result"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="-mt-2 mb-4">
        <button
          onClick={() => onUseQuery && onUseQuery(result.query)}
          className="text-sm text-gray-500 hover:text-green-600 hover:underline transition-colors cursor-pointer text-left block"
          title="Click to reuse this query"
          aria-label="Reuse this query"
        >
          {result.query}
        </button>
      </div>
      
      {(processedContent.descriptions.length > 0 || (result.isInProgress && streamingDescription)) && (
        <div className="mb-6 text-left">
          <div className="text-gray-700 prose prose-sm max-w-none">
            <ReactMarkdown>
              {processedContent.descriptions.length > 0
                ? processedContent.descriptions.join('\n\n')
                : streamingDescription}
            </ReactMarkdown>
          </div>
        </div>
      )}
      
      {processedContent.charts.length > 0 && (
        <div className="mb-6">
          <div className="space-y-4">
            {processedContent.charts.map((chart, chartIndex) => (
              <div key={chartIndex} className="overflow-auto">
                <img 
                  src={`data:image/png;base64,${chart}`} 
                  alt={`Survey data visualization ${chartIndex + 1}`} 
                  className="mx-auto"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      
      {processedContent.statistics && processedContent.statistics.statistics && (
        <div className="mb-6">
          <button
            onClick={toggleStatistics}
            className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
          >
            <h3 className="text-lg font-medium">Data</h3>
            <span className="text-gray-500">
              {isStatisticsExpanded ? '▼' : '▶'}
            </span>
          </button>
          
          {isStatisticsExpanded && (
            <div className="mt-2 bg-gray-50 p-4 rounded-lg overflow-auto">
              {DataRenderer.render(processedContent.statistics.statistics)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 